/**
 * WaveSpawnSystem - Handles dynamic wave spawn timing and quality
 *
 * Instead of fixed 100-mile intervals, waves spawn at variable distances
 * with a luck accumulator that creates drought/bounty patterns.
 *
 * Key mechanics:
 * - Spawn distance varies (70-150 miles base range)
 * - Luck accumulator biases toward opposite of recent pattern
 * - Longer waits = better quality floor
 * - Quality has additional random variance
 * - 25% of waves are type-specific (slot-locked)
 */

import { TYPE_SPECIFIC_LOT, LOT_OPEN_COST, QUALITY_TIER_THRESHOLDS } from '../config/balance.js';
import { pickupRegistry } from '../registries/PickupRegistry.js';
import { registerDefaultPickupTypes } from '../world/pickups/PickupTypes.js';

export class WaveSpawnSystem {
    constructor(gameState, itemsConfig) {
        this.gameState = gameState;
        this.itemsConfig = itemsConfig;

        // Base spawn distance configuration (P0 values)
        this.baseMinSpawnDistance = 70;   // Minimum miles between waves
        this.baseMaxSpawnDistance = 150;  // Maximum miles between waves
        this.baseSpawnDistance = 100;     // Center point for rolls

        // Prestige scaling for distance (+60% per prestige to maintain ~20-30 min pace)
        this.prestigeDistanceScale = 0.6;

        // Base quality configuration (P0 values)
        this.baseQualityFloor = 0.12;     // Minimum quality floor at P0
        this.maxQuality = 1.0;            // Maximum quality cap
        this.baseDistanceQualityScale = 0.0025; // Quality bonus per mile at P0
        this.qualityVariance = 0.08;      // Random variance (+/-)

        // Prestige scaling for quality
        this.prestigeQualityBonus = 0.10;       // +0.10 base quality per prestige
        this.prestigeDistanceBonusScale = 0.4;  // +40% distance bonus per prestige

        // Luck accumulator - tracks recent spawn pattern
        // Positive = recent spawns were short (due for a long one)
        // Negative = recent spawns were long (due for a short one)
        this.luckDecayRate = 0.3;     // How fast luck trends back to neutral
    }

    /**
     * Get current prestige level
     * @returns {number} Prestige level (0+)
     */
    getPrestigeLevel() {
        return this.gameState?.state?.prestige?.level || 0;
    }

    /**
     * Get prestige-scaled minimum spawn distance
     * @returns {number} Scaled min distance
     */
    get minSpawnDistance() {
        const prestige = this.getPrestigeLevel();
        return Math.round(this.baseMinSpawnDistance * (1 + prestige * this.prestigeDistanceScale));
    }

    /**
     * Get prestige-scaled maximum spawn distance
     * @returns {number} Scaled max distance
     */
    get maxSpawnDistance() {
        const prestige = this.getPrestigeLevel();
        return Math.round(this.baseMaxSpawnDistance * (1 + prestige * this.prestigeDistanceScale));
    }

    /**
     * Get prestige-scaled base quality
     * @returns {number} Scaled base quality
     */
    get baseQuality() {
        const prestige = this.getPrestigeLevel();
        return this.baseQualityFloor + (prestige * this.prestigeQualityBonus);
    }

    /**
     * Get prestige-scaled distance quality bonus rate
     * @returns {number} Scaled quality per mile
     */
    get distanceQualityScale() {
        const prestige = this.getPrestigeLevel();
        return this.baseDistanceQualityScale * (1 + prestige * this.prestigeDistanceBonusScale);
    }

    /**
     * Initialize wave state if needed (for new games or migration)
     */
    initializeWaveState() {
        const waves = this.gameState.state.waves;

        // Add new fields if they don't exist
        if (waves.nextWaveAtMile === undefined) {
            // Migrate from old system - use nextMilestone if it exists
            if (waves.nextMilestone !== undefined && waves.nextMilestone > 0) {
                waves.nextWaveAtMile = waves.nextMilestone;
                waves.nextWaveQuality = this.calculateQualityForDistance(100); // Assume standard distance
            } else {
                // Fresh start - first wave at 25 miles (tutorial wave)
                waves.nextWaveAtMile = 25;
                waves.nextWaveQuality = 0.1;
            }
        }

        if (waves.luckAccumulator === undefined) {
            waves.luckAccumulator = 0;
        }

        if (waves.lastSpawnDistance === undefined) {
            waves.lastSpawnDistance = 100;
        }

        // Initialize pickup type for current wave (default to 'wave' for backward compat)
        if (waves.nextPickupType === undefined) {
            waves.nextPickupType = 'wave';  // Tutorial and legacy always start as wave
        }
        // Migrate legacy object pickupType to string
        if (typeof waves.nextPickupType === 'object' && waves.nextPickupType?.id) {
            waves.nextPickupType = waves.nextPickupType.id;
        }

        // Initialize futureWaves array if missing
        if (!waves.futureWaves) {
            waves.futureWaves = [];
        }
        // Migrate legacy object pickupTypes in futureWaves to strings
        waves.futureWaves.forEach(wave => {
            if (typeof wave.pickupType === 'object' && wave.pickupType?.id) {
                wave.pickupType = wave.pickupType.id;
            }
        });

        // Ensure we always have at least one future wave pre-rolled
        this.ensureFutureWaves();

        // Keep nextMilestone in sync for backward compatibility with signs
        waves.nextMilestone = waves.nextWaveAtMile;

        this.gameState.save();
    }

    /**
     * Ensure there's always at least one future wave pre-rolled
     * This allows signs to show accurate distances even when current wave is uncollected
     */
    ensureFutureWaves() {
        const waves = this.gameState.state.waves;

        if (!waves.futureWaves) {
            waves.futureWaves = [];
        }

        // Pre-roll future waves if needed
        // We want at least 1 future wave beyond the current nextWaveAtMile
        while (waves.futureWaves.length < 1) {
            // Determine starting point for this future wave
            const lastWaveMile = waves.futureWaves.length > 0
                ? waves.futureWaves[waves.futureWaves.length - 1].atMile
                : waves.nextWaveAtMile;

            // Roll distance and quality for this future wave
            const luck = waves.luckAccumulator || 0;
            const distance = this.rollSpawnDistance(luck);
            const quality = this.calculateQualityForDistance(distance);

            // Update luck accumulator (simulating what would happen)
            const distanceFromBase = distance - this.baseSpawnDistance;
            const normalizedDeviation = distanceFromBase / (this.maxSpawnDistance - this.baseSpawnDistance);
            waves.luckAccumulator = Math.max(-1, Math.min(1,
                (waves.luckAccumulator * (1 - this.luckDecayRate)) - (normalizedDeviation * 0.5)
            ));

            // Roll pickup type (75% wave, 25% salvage-crate)
            const pickupType = this.rollPickupType();

            // Roll for type-specific lot (25% chance) - only for wave pickups
            const slotType = pickupType === 'wave' ? this.rollSlotType() : null;

            waves.futureWaves.push({
                atMile: lastWaveMile + distance,
                quality: quality,
                distance: distance,
                pickupType: pickupType,  // 'wave' or 'salvage-crate'
                slotType: slotType  // null for generic/salvage, string for type-specific
            });
        }
    }

    /**
     * Advance to the next wave after current one is collected
     * Consumes from futureWaves and ensures more are pre-rolled
     * @param {number} currentMiles - Current total miles when wave was collected (unused, kept for API compat)
     * @returns {object} - { distance, quality, nextWaveAtMile }
     */
    rollNextWave(currentMiles) {
        const waves = this.gameState.state.waves;

        // Initialize futureWaves if needed
        if (!waves.futureWaves) {
            waves.futureWaves = [];
        }

        // Ensure we have future waves to consume
        this.ensureFutureWaves();

        // Consume the next pre-rolled wave
        const nextWave = waves.futureWaves.shift();

        // Update current wave target
        waves.nextWaveAtMile = nextWave.atMile;
        waves.nextWaveQuality = nextWave.quality;
        waves.lastSpawnDistance = nextWave.distance;
        // Extract ID if pickupType is object (legacy save migration)
        const rawPickupType = nextWave.pickupType;
        waves.nextPickupType = (typeof rawPickupType === 'object' && rawPickupType?.id)
            ? rawPickupType.id
            : (rawPickupType || 'wave');
        waves.nextSlotType = nextWave.slotType || null;

        // Keep nextMilestone in sync for legacy compatibility
        waves.nextMilestone = waves.nextWaveAtMile;

        // Pre-roll more future waves
        this.ensureFutureWaves();

        this.gameState.save();

        return {
            distance: nextWave.distance,
            quality: nextWave.quality,
            nextWaveAtMile: waves.nextWaveAtMile,
            pickupType: waves.nextPickupType
        };
    }

    /**
     * Get the next upcoming wave target for signs
     * Returns the first uncollected wave (current if not yet reached, or first future)
     * @param {number} currentMiles - Current player miles
     * @returns {object} - { atMile, quality }
     */
    getNextWaveTarget(currentMiles) {
        const waves = this.gameState.state.waves;

        // If player hasn't reached current wave yet, return it
        if (currentMiles < waves.nextWaveAtMile) {
            return {
                atMile: waves.nextWaveAtMile,
                quality: waves.nextWaveQuality
            };
        }

        // Player has passed current wave (uncollected) - return first future wave
        if (waves.futureWaves && waves.futureWaves.length > 0) {
            return {
                atMile: waves.futureWaves[0].atMile,
                quality: waves.futureWaves[0].quality
            };
        }

        // Fallback - shouldn't happen but ensure future waves exist
        this.ensureFutureWaves();
        if (waves.futureWaves && waves.futureWaves.length > 0) {
            return {
                atMile: waves.futureWaves[0].atMile,
                quality: waves.futureWaves[0].quality
            };
        }

        // Ultimate fallback
        return {
            atMile: waves.nextWaveAtMile,
            quality: waves.nextWaveQuality
        };
    }

    /**
     * Roll spawn distance with luck influence
     * @param {number} luck - Current luck accumulator (-1 to 1)
     * @returns {number} - Distance in miles
     */
    rollSpawnDistance(luck) {
        // Base random roll (0 to 1)
        const baseRoll = Math.random();

        // Luck shifts the distribution
        // luck > 0 means we're due for a long spawn, shift roll upward
        // luck < 0 means we're due for a short spawn, shift roll downward
        const luckInfluence = luck * 0.3; // Max 30% shift
        const adjustedRoll = Math.max(0, Math.min(1, baseRoll + luckInfluence));

        // Map to distance range
        const range = this.maxSpawnDistance - this.minSpawnDistance;
        const distance = this.minSpawnDistance + (adjustedRoll * range);

        // Round to nearest mile
        return Math.round(distance);
    }

    /**
     * Calculate pack quality based on spawn distance
     * Longer distances = better quality floor
     * @param {number} distance - Spawn distance in miles
     * @returns {number} - Quality value (0.0 to 1.0)
     */
    calculateQualityForDistance(distance) {
        // Base quality from distance
        // 70 miles = base quality
        // 150 miles = base + (80 * 0.004) = base + 0.32
        const distanceBonus = (distance - this.minSpawnDistance) * this.distanceQualityScale;

        // Add random variance
        const variance = (Math.random() * 2 - 1) * this.qualityVariance;

        // Calculate final quality
        let quality = this.baseQuality + distanceBonus + variance;

        // Apply Wave Luck bonus from driver skills
        const waveLuckBonus = this.gameState?.getWaveLuckBonus() || 0;
        if (waveLuckBonus > 0) {
            quality *= (1 + waveLuckBonus);
        }

        // Clamp to valid range (minimum 0.04 allows deep faded lots)
        quality = Math.max(0.04, Math.min(this.maxQuality, quality));

        // Round to 2 decimal places
        return Math.round(quality * 100) / 100;
    }

    /**
     * Calculate cost for opening a pack based on quality tier
     * @param {number} quality - Pack quality (0.0 to 1.0)
     * @param {number} atMile - Mile marker where pack was collected (unused, kept for API compat)
     * @returns {number} - Cost in wavedust
     */
    calculatePackCost(quality, atMile) {
        // Use tier-based flat wavedust cost (5/10/15/20/25)
        const tier = this.getQualityTier(quality);
        return LOT_OPEN_COST[tier] || LOT_OPEN_COST.faded;
    }

    /**
     * Get the current next wave info
     * @returns {object} - { nextWaveAtMile, quality, distanceRemaining }
     */
    getNextWaveInfo() {
        const waves = this.gameState.state.waves;
        const currentMiles = this.gameState.getTotalMiles();

        return {
            nextWaveAtMile: waves.nextWaveAtMile || waves.nextMilestone || 25,
            quality: waves.nextWaveQuality || 0.1,
            distanceRemaining: Math.max(0, (waves.nextWaveAtMile || waves.nextMilestone || 25) - currentMiles)
        };
    }

    /**
     * Check if player has reached the next wave spawn point
     * @param {number} currentMiles - Current total miles
     * @returns {boolean}
     */
    hasReachedNextWave(currentMiles) {
        const waves = this.gameState.state.waves;
        const targetMile = waves.nextWaveAtMile || waves.nextMilestone || 25;
        return currentMiles >= targetMile;
    }

    /**
     * Get pack data for the current pending wave
     * @returns {object} - { totalMiles, cost, packQuality, isTutorial, pickupType, slotType }
     */
    getCurrentPackData() {
        const waves = this.gameState.state.waves;
        const atMile = waves.nextWaveAtMile || waves.nextMilestone || 25;
        const quality = waves.nextWaveQuality || 0.1;
        // Handle legacy saves where pickupType may be object instead of string
        const rawPickupType = waves.nextPickupType;
        const pickupType = (typeof rawPickupType === 'object' && rawPickupType?.id)
            ? rawPickupType.id
            : (rawPickupType || 'wave');
        const slotType = waves.nextSlotType || null;  // null for generic, string for type-specific

        // First pack is free and is the tutorial wave
        // Tutorial always spawns as wave type, not salvage crate
        const isTutorialWave = atMile <= 25 && !waves.tutorialShown;
        const cost = isTutorialWave ? 0 : this.calculatePackCost(quality, atMile);

        return {
            totalMiles: atMile,
            cost,
            packQuality: quality,
            milestone: atMile,
            isTutorial: isTutorialWave,
            pickupType: isTutorialWave ? 'wave' : pickupType,  // Force wave for tutorial
            slotType: isTutorialWave ? null : slotType  // No slot type for tutorial
        };
    }

    /**
     * Get quality tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Tier name
     */
    getQualityTier(quality) {
        if (quality < 0.2) return 'faded';
        if (quality < 0.4) return 'standard';
        if (quality < 0.6) return 'pristine';
        if (quality < 0.8) return 'golden';
        return 'vectorized';
    }

    /**
     * Get quality color from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} - Hex color
     */
    getQualityColor(quality) {
        const colors = this.itemsConfig.rarityColors;
        const tier = this.getQualityTier(quality);
        return colors[tier] || colors.faded;
    }

    /**
     * Roll for type-specific lot slot
     * 25% of wave pickups are type-specific (slot-locked)
     * @returns {string|null} - Slot type ('rearview', 'dashboard', 'keychain') or null for generic
     */
    rollSlotType() {
        // Roll for type-specific (25% chance)
        if (Math.random() < TYPE_SPECIFIC_LOT.RATE) {
            // Randomly select one of the slot types
            const slots = TYPE_SPECIFIC_LOT.SLOT_TYPES;
            const index = Math.floor(Math.random() * slots.length);
            const slotType = slots[index];
            console.log(`[WaveSpawnSystem] Rolled type-specific lot: ${slotType} (index ${index} of ${slots.length}: ${slots.join(', ')})`);
            return slotType;
        }
        return null;  // Generic lot
    }

    /**
     * Roll for pickup type using PickupRegistry weights
     * Uses weighted random selection: 75% wave, 25% salvage-crate
     * @returns {string} - Pickup type id ('wave' or 'salvage-crate')
     */
    rollPickupType() {
        // Ensure default pickup types are registered
        registerDefaultPickupTypes(pickupRegistry);

        // Use registry's weighted random selection
        const type = pickupRegistry.getWeightedRandomType();
        return type ? type.id : 'wave';  // Default to 'wave' if registry empty
    }
}
